import { useState, useEffect, useCallback, useRef } from 'react';
import { CostData, CostFilters, CostError } from '../types';
import { costService } from '../services/costService';

interface UseCostDataOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  useMockData?: boolean;
}

interface UseCostDataReturn {
  data: CostData | null;
  loading: boolean;
  error: CostError | null;
  refetch: () => Promise<void>;
  lastUpdated: Date | null;
  applyFilters: (filters: CostFilters) => void;
  clearFilters: () => void;
  currentFilters: CostFilters;
}

export const useCostData = (
  options: UseCostDataOptions = {}
): UseCostDataReturn => {
  const {
    autoRefresh = true,
    refreshInterval = 300000, // 5 minutes
    useMockData = true // Use mock data by default for development
  } = options;

  const [data, setData] = useState<CostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<CostError | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [currentFilters, setCurrentFilters] = useState<CostFilters>({});

  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async (filters?: CostFilters): Promise<void> => {
    if (!isMountedRef.current) return;

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      console.log('CostData: Starting cost data fetch...', { filters, useMockData });
      
      let costData: CostData;

      if (useMockData) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if component is still mounted and request not aborted
        if (!isMountedRef.current || abortControllerRef.current?.signal.aborted) {
          return;
        }
        
        costData = costService.generateMockCostData();
        console.log('CostData: Mock data generated:', costData);
      } else {
        // Real API call
        const token = localStorage.getItem('auth_token');
        if (!token) {
          throw new Error('Authentication required - no auth token found');
        }

        costData = await costService.getCostSummary(filters);
        console.log('CostData: Real data fetched:', costData);
      }

      // Apply client-side filtering if needed
      if (filters && Object.keys(filters).length > 0) {
        costData = applyClientSideFilters(costData, filters);
        console.log('CostData: Filters applied:', filters);
      }

      if (isMountedRef.current && !abortControllerRef.current?.signal.aborted) {
        setData(costData);
        setLastUpdated(new Date());
        console.log('CostData: Data successfully set to state');
      }

    } catch (error: any) {
      console.error('CostData: Error fetching cost data:', error);
      
      if (error.name === 'AbortError') {
        console.log('CostData: Request was aborted');
        return;
      }
      
      if (isMountedRef.current) {
        setError({
          code: error.code || 'COST_FETCH_ERROR',
          message: error.message || 'Failed to fetch cost data',
          service: 'multiple',
          timestamp: new Date().toISOString()
        });
        
        // Don't clear data on error - keep showing last successful data
        if (!data) {
          setData(null);
        }
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        console.log('CostData: Loading state set to false');
      }
    }
  }, [useMockData, data]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (autoRefresh && refreshInterval > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          fetchData(currentFilters);
        }
      }, refreshInterval);
    }
  }, [autoRefresh, refreshInterval, fetchData, currentFilters]);

  const refetch = useCallback(async (): Promise<void> => {
    // Clear existing timeout before manual refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    await fetchData(currentFilters);
    
    // Schedule next refresh
    scheduleRefresh();
  }, [fetchData, scheduleRefresh, currentFilters]);

  const applyFilters = useCallback((filters: CostFilters) => {
    console.log('CostData: Applying filters:', filters);
    setCurrentFilters(filters);
    
    // Clear existing timeout and fetch with new filters
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    fetchData(filters).finally(() => {
      if (isMountedRef.current) {
        scheduleRefresh();
      }
    });
  }, [fetchData, scheduleRefresh]);

  const clearFilters = useCallback(() => {
    console.log('CostData: Clearing filters');
    setCurrentFilters({});
    
    // Clear existing timeout and fetch without filters
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    fetchData({}).finally(() => {
      if (isMountedRef.current) {
        scheduleRefresh();
      }
    });
  }, [fetchData, scheduleRefresh]);

  // Initial data fetch
  useEffect(() => {
    console.log('CostData: useEffect triggered, starting initial fetch');
    
    // Reset mounted ref to true
    isMountedRef.current = true;
    
    // Start loading immediately
    setLoading(true);
    
    // Fetch data
    fetchData().finally(() => {
      console.log('CostData: Initial fetch completed');
      if (isMountedRef.current) {
        scheduleRefresh();
      }
    });

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []); // Remove dependencies to prevent infinite loop

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    lastUpdated,
    applyFilters,
    clearFilters,
    currentFilters
  };
};

// Helper function to apply client-side filters
function applyClientSideFilters(data: CostData, filters: CostFilters): CostData {
  const filteredData = { ...data };

  // Filter by cloud providers
  if (filters.cloud?.length) {
    const allowedClouds = filters.cloud;
    
    if (!allowedClouds.includes('aws')) {
      filteredData.clouds.aws = {
        accounts: [],
        regions: [],
        services: [],
        totalSpend: 0
      };
    }
    
    if (!allowedClouds.includes('gcp')) {
      filteredData.clouds.gcp = {
        projects: [],
        regions: [],
        services: [],
        totalSpend: 0
      };
    }
    
    if (!allowedClouds.includes('github')) {
      filteredData.clouds.github = {
        orgs: [],
        usage: [],
        totalSpend: 0
      };
    }
  }

  // Filter by cost range
  if (filters.costRange) {
    const { min, max } = filters.costRange;
    
    // Filter AWS accounts
    filteredData.clouds.aws.accounts = filteredData.clouds.aws.accounts.filter(
      account => account.totalCost >= min && account.totalCost <= max
    );
    
    // Filter GCP projects
    filteredData.clouds.gcp.projects = filteredData.clouds.gcp.projects.filter(
      project => project.totalCost >= min && project.totalCost <= max
    );
    
    // Filter GitHub orgs
    filteredData.clouds.github.orgs = filteredData.clouds.github.orgs.filter(
      org => org.totalCost >= min && org.totalCost <= max
    );
  }

  // Filter by regions
  if (filters.regions?.length) {
    const allowedRegions = filters.regions;
    
    filteredData.clouds.aws.regions = filteredData.clouds.aws.regions.filter(
      region => allowedRegions.includes(region.region)
    );
    
    filteredData.clouds.gcp.regions = filteredData.clouds.gcp.regions.filter(
      region => allowedRegions.includes(region.region)
    );
  }

  // Filter by accounts
  if (filters.accounts?.length) {
    const allowedAccounts = filters.accounts;
    
    filteredData.clouds.aws.accounts = filteredData.clouds.aws.accounts.filter(
      account => allowedAccounts.includes(account.accountId) || allowedAccounts.includes(account.accountName)
    );
    
    filteredData.clouds.gcp.projects = filteredData.clouds.gcp.projects.filter(
      project => allowedAccounts.includes(project.projectId) || allowedAccounts.includes(project.projectName)
    );
    
    filteredData.clouds.github.orgs = filteredData.clouds.github.orgs.filter(
      org => allowedAccounts.includes(org.orgName)
    );
  }

  // Recalculate summary after filtering
  filteredData.summary.totalSpend = 
    filteredData.clouds.aws.totalSpend + 
    filteredData.clouds.gcp.totalSpend + 
    filteredData.clouds.github.totalSpend;

  // Recalculate byType based on filtered data
  const computeCost = 
    (filteredData.clouds.aws.services.find(s => s.serviceName === 'EC2')?.totalCost || 0) +
    (filteredData.clouds.gcp.services.find(s => s.serviceName === 'Compute Engine')?.totalCost || 0);
  
  const storageCost = 
    (filteredData.clouds.aws.services.find(s => s.serviceName === 'S3')?.totalCost || 0) +
    (filteredData.clouds.gcp.services.find(s => s.serviceName === 'Cloud Storage')?.totalCost || 0);

  filteredData.summary.byType = {
    compute: computeCost,
    storage: storageCost,
    networking: filteredData.summary.byType.networking, // Keep original
    github: filteredData.clouds.github.totalSpend,
    other: filteredData.summary.totalSpend - computeCost - storageCost - filteredData.clouds.github.totalSpend
  };

  return filteredData;
}